import { useMemo, useState, type CSSProperties } from 'react'
import { Link, useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  Button,
  CommitList as FrmCommitList,
  DiffViewer,
  EmptyState,
  ErrorBanner,
  FileViewer as FrmFileViewer,
  MetricStrip,
  PageHeader,
  PageToolbar,
  Pill,
  Skeleton,
  SmartTable,
  StatusPill,
  Surface,
  TableFilterBar,
  Tabs,
  VaultTree,
  Well,
  WorkbenchPanel,
  type DiffLine,
  type DiffLineKind,
  type FileTreeNode,
  type SmartColumn,
  type TableFilterField,
  type TableFilterValues,
} from '@frm/ui'

import {
  fetchVaultCommits,
  fetchVaultDiff,
  fetchVaultFile,
  fetchVaults,
  fetchVaultTree,
  vaultPath,
  type VaultAccess,
  type VaultCommitDto,
  type VaultDiffDto,
  type VaultDto,
  type VaultFileDto,
  type VaultTreeNodeDto,
} from '../api/vaults'
import { formatAge, formatInteger, MONO_NUMERIC_STYLE } from '../shared/format'
import { InProgressAction } from '../shared/in-progress'

type VaultFilter = VaultAccess | 'archived' | 'all'

const VAULT_FILTER_FIELDS: TableFilterField[] = [
  { key: 'query', kind: 'search', label: 'Поиск', placeholder: 'Имя, git, clone path' },
  {
    key: 'access',
    kind: 'select',
    label: 'Доступ',
    options: [
      { value: 'all', label: 'Все хранилища' },
      { value: 'RW', label: 'RW' },
      { value: 'RO', label: 'RO' },
      { value: 'archived', label: 'Архив' },
    ],
  },
]

const PAGE_STYLE: CSSProperties = {
  display: 'grid',
  gap: 16,
  minWidth: 0,
}

const GRID_STYLE: CSSProperties = {
  alignItems: 'start',
  display: 'grid',
  gap: 16,
  gridTemplateColumns: 'minmax(260px, 360px) minmax(0, 1fr)',
}

const STACK_STYLE: CSSProperties = {
  display: 'grid',
  gap: 12,
}

const ROW_STYLE: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  gap: 10,
  justifyContent: 'space-between',
  minWidth: 0,
}

const MUTED_STYLE: CSSProperties = {
  color: 'var(--frm-text-muted, #94a3b8)',
}

function accessTone(vault: Pick<VaultDto, 'access' | 'archived'>) {
  if (vault.archived) return 'warning' as const
  return vault.access === 'RW' ? 'success' as const : 'neutral' as const
}

function matchesVault(vault: VaultDto, query: string, filter: VaultFilter): boolean {
  if (filter === 'archived' && !vault.archived) return false
  if (filter === 'RW' && (vault.archived || vault.access !== 'RW')) return false
  if (filter === 'RO' && (vault.archived || vault.access !== 'RO')) return false
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  return [vault.name, vault.git, vault.clone, vault.desc].join(' ').toLowerCase().includes(needle)
}

function parseVaultPath(path?: string): { vaultId: string | undefined; filePath: string | undefined } {
  if (!path) return { vaultId: undefined, filePath: undefined }
  const [vaultId, ...rest] = path.split('/')
  return {
    vaultId: vaultId ? decodeURIComponent(vaultId) : undefined,
    filePath: rest.length ? rest.map(part => decodeURIComponent(part)).join('/') : undefined,
  }
}

function treeFileCount(nodes: VaultTreeNodeDto[]): number {
  return nodes.reduce((sum, node) => sum + (node.type === 'file' ? 1 : treeFileCount(node.children ?? [])), 0)
}

function diffLineKind(line: VaultDiffDto['files'][number]['lines'][number]): DiffLineKind {
  if (line.kind === 'added' || line.sign === '+') return 'added'
  if (line.kind === 'removed' || line.sign === '-') return 'removed'
  if (line.kind === 'changed') return 'changed'
  return 'context'
}

function diffLines(diff: VaultDiffDto | undefined): DiffLine[] {
  if (!diff) return []
  return diff.files.flatMap((file, fileIndex) => [
    { id: `${fileIndex}:header`, kind: 'context' as const, content: file.path },
    ...file.lines.map((line, lineIndex) => ({
      id: `${fileIndex}:${lineIndex}`,
      kind: diffLineKind(line),
      content: `${line.gut} ${line.code}`.trimEnd(),
    })),
  ])
}

function vaultTreeNodes(nodes: VaultTreeNodeDto[]): FileTreeNode[] {
  return nodes.map((node) => ({
    id: node.path || node.name,
    name: node.name,
    path: node.path,
    kind: node.type === 'dir' ? 'dir' : 'file',
    type: node.type,
    badge: node.ann || undefined,
    children: node.children ? vaultTreeNodes(node.children) : undefined,
  }))
}

function VaultTable({ rows }: { rows: VaultDto[] }) {
  const columns = useMemo<SmartColumn<VaultDto>[]>(
    () => [
      {
        key: 'vault',
        header: 'Хранилище · git',
        searchable: true,
        render: (row) => (
          <div style={{ display: 'grid', gap: 5, minWidth: 0 }}>
            <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <Link to={vaultPath(row.id)} style={{ color: 'inherit', textDecoration: 'none' }}>
                <strong>{row.name}</strong>
              </Link>
              {row.archived ? <Pill tone="warning" size="sm">archive</Pill> : null}
            </div>
            <code style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-muted, #94a3b8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.git || 'origin не задан'}
            </code>
            {row.desc ? <span style={{ ...MUTED_STYLE, fontSize: 12 }}>{row.desc}</span> : null}
          </div>
        ),
      },
      {
        key: 'clone',
        header: 'Clone · sync',
        searchable: true,
        render: (row) => (
          <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
            <code style={{ ...MONO_NUMERIC_STYLE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.clone}
            </code>
            <span style={MUTED_STYLE}>{formatAge(row.sync)}</span>
          </div>
        ),
      },
      {
        key: 'access',
        header: 'Доступ',
        width: 128,
        render: (row) => (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <Pill tone={accessTone(row)}>{row.access}</Pill>
            {row.archived ? <Pill tone="warning">no projects</Pill> : null}
          </div>
        ),
        sortAccessor: (row) => row.archived ? 'archive' : row.access,
      },
      {
        key: 'ahead',
        header: 'Ahead',
        width: 82,
        align: 'right',
        render: (row) => (
          <Pill tone={row.ahead > 0 ? 'warning' : 'success'}>{row.ahead > 0 ? `+${row.ahead}` : 'synced'}</Pill>
        ),
        sortAccessor: (row) => row.ahead,
      },
      {
        key: 'actions',
        header: 'Действия',
        width: 230,
        align: 'right',
        render: (row) => (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Link
              to={vaultPath(row.id)}
              style={{
                alignItems: 'center',
                border: '1px solid rgba(148, 163, 184, 0.24)',
                borderRadius: 8,
                color: 'inherit',
                display: 'inline-flex',
                minHeight: 32,
                padding: '0 10px',
                textDecoration: 'none',
              }}
            >
              Открыть
            </Link>
            {!row.archived ? (
              <>
                <InProgressAction
                  id="vaults.pull"
                  render={(props) => (
                    <Button {...props} onClick={(event) => props.onClick(event)} onKeyDown={(event) => props.onKeyDown(event)} size="sm" variant="ghost">
                      Pull
                    </Button>
                  )}
                />
                <InProgressAction
                  id="vaults.edit"
                  render={(props) => (
                    <Button {...props} onClick={(event) => props.onClick(event)} onKeyDown={(event) => props.onKeyDown(event)} size="sm" variant="ghost">
                      Edit
                    </Button>
                  )}
                />
              </>
            ) : null}
          </div>
        ),
      },
    ],
    [],
  )

  return (
    <SmartTable
      title="Хранилища"
      description="Live список из /api/vaults. Архивные roots видны здесь, но не участвуют в поиске проектов."
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      density="compact"
      pageSize={5}
      pageSizeOptions={[5, 10]}
      defaultSortKey="access"
      ariaLabel="Список хранилищ"
      emptyTitle="Хранилища не найдены"
      emptyDescription="Измените поиск или фильтр."
    />
  )
}

export function VaultsPage() {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<VaultFilter>('all')
  const vaultsQuery = useQuery({
    queryKey: ['vaults'],
    queryFn: fetchVaults,
    refetchInterval: 60_000,
  })

  const vaults = vaultsQuery.data ?? []
  const rows = useMemo(() => vaults.filter((vault) => matchesVault(vault, query, filter)), [filter, query, vaults])
  const rwCount = vaults.filter((vault) => vault.access === 'RW' && !vault.archived).length
  const archivedCount = vaults.filter((vault) => vault.archived).length
  const aheadTotal = vaults.reduce((sum, vault) => sum + vault.ahead, 0)
  const activeProjectsRoots = vaults.length - archivedCount

  return (
    <div style={PAGE_STYLE}>
      <PageHeader
        eyebrow="Раздел · Хранилища"
        title="Хранилища"
        description="Git-хранилища знаний и кода: список, доступ, sync-state и browser дерева. Старый root vault помечается архивом."
        meta={vaultsQuery.isFetching ? <Pill tone="info">обновление</Pill> : <Pill tone="success">live /api/vaults</Pill>}
      />
      <MetricStrip
        minColumnWidth={170}
        items={[
          { id: 'connected', label: 'Подключено', value: formatInteger(vaults.length) },
          { id: 'active', label: 'Ищем проекты', value: formatInteger(activeProjectsRoots), tone: activeProjectsRoots ? 'success' : 'neutral' },
          { id: 'archived', label: 'Архив', value: formatInteger(archivedCount), tone: archivedCount ? 'warning' : 'neutral' },
          { id: 'ahead', label: 'Невыпущено', value: `+${formatInteger(aheadTotal)}`, tone: aheadTotal ? 'warning' : 'neutral' },
          { id: 'rw', label: 'RW', value: formatInteger(rwCount), tone: rwCount ? 'success' : 'neutral' },
        ]}
      />
      <TableFilterBar
        fields={VAULT_FILTER_FIELDS}
        values={{ query: query || undefined, access: filter }}
        onChange={(next: TableFilterValues) => {
          setQuery(typeof next.query === 'string' ? next.query : '')
          setFilter(next.access === 'RW' || next.access === 'RO' || next.access === 'archived' ? next.access : 'all')
        }}
        onReset={() => {
          setQuery('')
          setFilter('all')
        }}
        resetLabel="Сбросить"
        activeFiltersLabel="Фильтры хранилищ"
        hideActiveChips
        density="compact"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" loading={vaultsQuery.isFetching} onClick={() => void vaultsQuery.refetch()}>
              Обновить
            </Button>
            <InProgressAction
              id="vaults.edit"
              render={(props) => (
                <Button {...props} onClick={(event) => props.onClick(event)} onKeyDown={(event) => props.onKeyDown(event)} variant="primary">
                  Добавить
                </Button>
              )}
            />
          </div>
        }
        layout={{ maxRows: 1 }}
      />
      {vaultsQuery.isLoading ? (
        <WorkbenchPanel>
          <Skeleton height={320} />
        </WorkbenchPanel>
      ) : vaultsQuery.isError ? (
        <ErrorBanner
          title="Не удалось загрузить хранилища"
          description={vaultsQuery.error instanceof Error ? vaultsQuery.error.message : 'unknown error'}
          action={<Button onClick={() => void vaultsQuery.refetch()}>Повторить</Button>}
        />
      ) : (
        <VaultTable rows={rows} />
      )}
      <Surface padding="md" variant="subtle">
        <span style={MUTED_STYLE}>
          Архивный `vault` остаётся доступен для просмотра файлов, но backend исключает его из `/api/projects`.
          Token fields остаются write-only; значения секретов не возвращаются в UI.
        </span>
      </Surface>
    </div>
  )
}

export function VaultWorkbench({ splatPath }: { splatPath?: string }) {
  const navigate = useNavigate()
  const [view, setView] = useState<'render' | 'raw'>('render')
  const { vaultId, filePath } = parseVaultPath(splatPath)

  const vaultsQuery = useQuery({
    queryKey: ['vaults'],
    queryFn: fetchVaults,
    refetchInterval: 60_000,
  })
  const vault = useMemo(() => {
    const vaults = vaultsQuery.data ?? []
    return vaults.find((item) => item.id === vaultId) ?? vaults[0] ?? null
  }, [vaultId, vaultsQuery.data])

  const treeQuery = useQuery({
    queryKey: ['vaults', vault?.id, 'tree'],
    queryFn: () => fetchVaultTree(vault?.id ?? '', 2),
    enabled: Boolean(vault),
  })
  const fileQuery = useQuery({
    queryKey: ['vaults', vault?.id, 'file', filePath],
    queryFn: () => fetchVaultFile(vault?.id ?? '', filePath ?? ''),
    enabled: Boolean(vault && filePath),
  })
  const commitsQuery = useQuery({
    queryKey: ['vaults', vault?.id, 'commits', filePath ?? ''],
    queryFn: () => fetchVaultCommits(vault?.id ?? '', filePath ?? ''),
    enabled: Boolean(vault),
  })
  const selectedCommit = commitsQuery.data?.[0] ?? null
  const diffQuery = useQuery({
    queryKey: ['vaults', vault?.id, 'diff', selectedCommit?.hash ?? ''],
    queryFn: () => fetchVaultDiff(vault?.id ?? '', selectedCommit?.hash ?? ''),
    enabled: Boolean(vault && selectedCommit),
  })

  if (vaultsQuery.isLoading) {
    return (
      <div style={PAGE_STYLE}>
        <WorkbenchPanel>
          <Skeleton height={460} />
        </WorkbenchPanel>
      </div>
    )
  }

  if (vaultsQuery.isError || !vault) {
    return (
      <ErrorBanner
        title="Хранилище не найдено"
        description={vaultsQuery.error instanceof Error ? vaultsQuery.error.message : 'Список хранилищ пуст.'}
        action={<Link to="/vaults">Вернуться к списку</Link>}
      />
    )
  }

  const tree = treeQuery.data ?? []
  const file = fileQuery.data ?? null
  const commits = commitsQuery.data ?? []

  return (
    <div style={PAGE_STYLE}>
      <PageHeader
        eyebrow="Vault browser"
        title={vault.name}
        description={vault.archived ? 'Архивное хранилище: просмотр доступен, поиск проектов отключён.' : 'Дерево, просмотр файлов, commits и read-only diff из live API.'}
        meta={<Pill tone={accessTone(vault)}>{vault.archived ? 'archive · no projects' : vault.access}</Pill>}
      />

      <Surface padding="md" variant="subtle">
        <div style={{ ...ROW_STYLE, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Link to="/vaults" style={{ color: 'inherit' }}>Хранилища</Link>
            <span style={MUTED_STYLE}>/</span>
            <code style={MONO_NUMERIC_STYLE}>{vault.name}</code>
            {filePath ? (
              <>
                <span style={MUTED_STYLE}>/</span>
                <code style={MONO_NUMERIC_STYLE}>{filePath}</code>
              </>
            ) : null}
          </div>
          {!vault.archived ? (
            <InProgressAction
              id="vaults.pull"
              render={(props) => (
                <Button {...props} onClick={(event) => props.onClick(event)} onKeyDown={(event) => props.onKeyDown(event)} size="sm" variant="secondary">
                  Подтянуть
                </Button>
              )}
            />
          ) : <Pill tone="warning">архив</Pill>}
        </div>
      </Surface>

      <MetricStrip
        minColumnWidth={170}
        items={[
          { id: 'ahead', label: 'Ahead', value: `+${vault.ahead}`, tone: vault.ahead ? 'warning' : 'neutral' },
          { id: 'files', label: 'Файлы в дереве', value: formatInteger(treeFileCount(tree)) },
          { id: 'commits', label: 'Коммиты', value: formatInteger(commits.length) },
          { id: 'sync', label: 'Last sync', value: formatAge(vault.sync) },
        ]}
      />

      <div style={GRID_STYLE}>
        <WorkbenchPanel title="VaultTree" description="tree -L 2 с git-аннотациями">
          {treeQuery.isLoading ? (
            <div style={STACK_STYLE}>
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
            </div>
          ) : treeQuery.isError ? (
            <ErrorBanner
              title="Не удалось открыть дерево"
              description={treeQuery.error instanceof Error ? treeQuery.error.message : 'unknown error'}
            />
          ) : tree.length === 0 ? (
            <EmptyState title="Дерево пустое" description="Backend не вернул файлы для этого хранилища." />
          ) : (
            <VaultTree
              nodes={vaultTreeNodes(tree)}
              onSelect={(node) => node.path ? navigate(vaultPath(vault.id, node.path)) : undefined}
              renderBadge={(node) => node.badge}
            />
          )}
        </WorkbenchPanel>

        <div style={STACK_STYLE}>
          <WorkbenchPanel
            title="FileViewer"
            description={filePath ?? 'Выберите файл в дереве'}
            actions={
              <Tabs
                value={view}
                onChange={(id) => setView(id as 'render' | 'raw')}
                ariaLabel="Режим просмотра файла"
                items={[
                  { id: 'render', label: 'Рендер' },
                  { id: 'raw', label: 'Raw' },
                ]}
              />
            }
          >
            <FrmFileViewer
              file={file ? {
                path: file.path,
                type: file.type,
                content: file.content,
                size: file.size,
                lastCommit: file.lastCommit,
              } : null}
              loading={fileQuery.isLoading}
              error={fileQuery.error instanceof Error ? fileQuery.error.message : fileQuery.error ? 'unknown error' : undefined}
              mode={view}
            />
          </WorkbenchPanel>

          <WorkbenchPanel title="Последние коммиты" description={filePath ? `git log -- ${filePath}` : 'git log root'}>
            <FrmCommitList
              commits={commits.map((commit) => ({
                hash: commit.hash,
                subject: commit.subj,
                author: commit.author,
                date: formatAge(commit.date),
                unreleased: commit.unreleased,
              }))}
              loading={commitsQuery.isLoading}
              error={commitsQuery.error instanceof Error ? commitsQuery.error.message : commitsQuery.error ? 'unknown error' : undefined}
              emptyText="Для выбранного пути коммиты не найдены."
            />
          </WorkbenchPanel>

          {selectedCommit ? (
            diffQuery.isLoading ? (
              <Skeleton height={180} />
            ) : diffQuery.isError ? (
              <ErrorBanner
                title="Не удалось открыть diff"
                description={diffQuery.error instanceof Error ? diffQuery.error.message : 'unknown error'}
              />
            ) : (
              <DiffViewer
                title="Diff коммита"
                description={`${selectedCommit.hash} · ${selectedCommit.subj}`}
                lines={diffLines(diffQuery.data)}
                compact
                ariaLabel="Diff выбранного коммита"
              />
            )
          ) : (
            <Well>
              <span style={MUTED_STYLE}>Выберите файл или дождитесь git log, чтобы увидеть diff.</span>
            </Well>
          )}
        </div>
      </div>
    </div>
  )
}
